import { create } from 'zustand'

/** The marketing (Dupe Drop) opt-in choice captured on the sign-up screen, in
 *  the `account_creation` arm (DUP-1903), before the account exists. */
export interface PendingMarketingConsent {
  /** Where the ask was shown. Always the sign-up screen for this path. */
  surface: 'signup_screen'
  /** Whether the shopper is opted in. The sign-up widget always renders a
   *  checkbox; geo only sets its INITIAL state: in implied-consent markets
   *  (US/RoW) it starts pre-ticked (`true`, a lawful pre-tick the shopper can
   *  still untick), and in active-consent markets (EEA/UK/CA) it starts unticked
   *  (`false`) so only a deliberate tick flips it. */
  optedIn: boolean
  /** Was an affirmative box required (EEA/UK/CA)? Drives `method` in telemetry
   *  and whether `optedIn` came from a tap vs. the notice default. */
  requiresActiveOptIn: boolean
  /** Resolved IP country (lower-case), for the consent jurisdiction record. */
  countryCode?: string
}

/**
 * The decision, as it will be sent to `POST /api/secure/marketing-consent`.
 *
 * Held separately from `consent` because the two have different lifetimes: the
 * captured choice stops being applicable the moment it has been ACTED on
 * (subscribed + tracked), while the ledger write may still be outstanding.
 * Keeping them in one field meant a failed ledger write left the choice
 * applicable, and the next mount re-ran the whole flush — a second Beehiiv
 * subscribe and a second `Newsletter Subscribed` event, which is DUP-1903's
 * primary metric.
 */
export interface PendingLedgerWrite {
  /** The account it belongs to. A pending write is never applied to a different
   *  account — same cross-account-leak risk the capture itself has. */
  uid: string
  decision: {
    granted: boolean
    subscribed: boolean
    surface: 'signup_screen'
    method: 'active' | 'implied'
    jurisdiction: string | null
    optinPlacement: 'account_creation'
  }
}

interface PendingMarketingConsentState {
  /** The captured choice, or null when the ask isn't on screen (other arms). */
  consent: PendingMarketingConsent | null
  /** Stamped by `AuthProvider` at the new-account moment (maybe-register
   *  `existing === false`). Gates the flush so only NEW registrations act on the
   *  captured choice — a returning sign-in never re-subscribes. */
  registeredAt: number | null
  /** When `consent` was captured. Used to refuse a stale capture at the moment
   *  it would become applicable — see `markRegistered`. */
  capturedAt: number | null
  /** A consent-ledger write that has been attempted and not yet confirmed. */
  pendingLedgerWrite: PendingLedgerWrite | null
  setConsent: (consent: PendingMarketingConsent) => void
  /**
   * The captured choice has been ACTED on — subscribed where applicable, and
   * tracked. Drops the choice (so nothing can re-run the flush) and hands over
   * the ledger write, which may still need retrying.
   */
  markApplied: (pending: PendingLedgerWrite) => void
  /** The ledger write landed. */
  clearLedgerWrite: () => void
  /** Stamp the new-account moment. REFUSES a capture older than
   *  {@link MAX_PENDING_CONSENT_AGE_MS}, clearing it instead, so a capture
   *  stranded by some exit path we didn't think of can never be applied to a
   *  later registration. Returns whether it stamped. */
  markRegistered: (at: number) => boolean
  clear: () => void
}

/**
 * How long a pre-auth capture stays applicable.
 *
 * The store is a module-level singleton with no owner: the choice is made BEFORE
 * an account exists, so there is no id to bind it to. Every exit path therefore
 * has to clear it, and the ones that were missed produced a real cross-account
 * leak — a returning sign-in (`existing === true`) never stamps `registeredAt`
 * and never cleared, so the next genuine registration in that tab could be
 * stamped with a stranded stranger's choice, subscribing them on someone else's
 * answer and misattributing jurisdiction and consent basis on the telemetry.
 *
 * The explicit clears (dismiss, sign-out, existing-user login) close the paths we
 * know about. This bound closes the ones we don't: a capture is only ever applied
 * within a few minutes of being made, which is the entire window a real sign-up
 * takes. Not a substitute for the clears — a backstop for them.
 */
export const MAX_PENDING_CONSENT_AGE_MS = 10 * 60 * 1000

/**
 * Holds the pre-auth marketing-consent choice so it survives account creation
 * and is applied once the user exists — the same capture-before-auth /
 * apply-after-auth shape as the invite-code flow (`useAutoRedeemInvite`).
 *
 * The sign-up widget writes `consent`; `AuthProvider` stamps `registeredAt` on a
 * brand-new account; `useFlushMarketingConsent` reacts to both being present and
 * subscribes via the existing newsletter rail, then hands the decision to
 * `pendingLedgerWrite` until the consent ledger has it. No persistence layer of
 * its own: it survives a remount, not a reload.
 */
export const usePendingMarketingConsentStore =
  create<PendingMarketingConsentState>((set, get) => ({
    consent: null,
    registeredAt: null,
    capturedAt: null,
    pendingLedgerWrite: null,
    setConsent: (consent) => set({ consent, capturedAt: Date.now() }),
    markApplied: (pending) =>
      set({
        consent: null,
        registeredAt: null,
        capturedAt: null,
        pendingLedgerWrite: pending,
      }),
    clearLedgerWrite: () => set({ pendingLedgerWrite: null }),
    markRegistered: (at) => {
      const { consent, capturedAt } = get()
      if (!consent) return false
      const age =
        capturedAt === null ? Number.POSITIVE_INFINITY : at - capturedAt
      if (age > MAX_PENDING_CONSENT_AGE_MS) {
        // Too old to be this registration's choice — drop it rather than apply it.
        set({ consent: null, registeredAt: null, capturedAt: null })
        return false
      }
      set({ registeredAt: at })
      return true
    },
    // Deliberately leaves `pendingLedgerWrite` alone: an abandoned capture is
    // not a reason to discard a decision that has already been acted on and
    // only needs recording.
    clear: () => set({ consent: null, registeredAt: null, capturedAt: null }),
  }))

/**
 * Should a modal dismiss drop the captured consent as ABANDONED?
 *
 * Only when NOBODY IS AUTHENTICATED. `registeredAt` alone is not enough, and
 * relying on it silently loses real consent: registration is stamped from
 * `maybeRegisterUser`'s async response, but on `skipOnboarding` entries
 * (savings calculator, trade/projects, the vanity-URL modal) `AuthContent`
 * closes the modal the instant auth succeeds — before that response lands. The
 * dismiss would then clear the capture, and `markRegistered` is itself gated on
 * `consent` still being present, so it never stamps: the shopper ticked the box,
 * registered, and is never subscribed.
 *
 * An authenticated user at dismiss time means registration is done or in
 * flight, and `useFlushMarketingConsent` owns the capture — leave it alone. It
 * clears the store itself once applied.
 */
export const shouldDropAbandonedConsent = ({
  authenticated,
  registeredAt,
}: {
  authenticated: boolean
  registeredAt: number | null
}): boolean => !authenticated && registeredAt === null
